import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { ApiError } from 'types/api/ApiError';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';

type QuickQuoteSuggestionsPayload = {
  suggestions: SuggestedQuoteChange[];
  meta?: { truncated?: boolean };
};

// With includeHandled, Kit gets every lane (including replied / quoted / skipped) as
// { suggestions, meta? }. Without it, the API returns only active lanes as a plain array.
// Handle both so we don't break against an older API that ignores the param.
export async function getQuickQuoteSuggestion(
  threadID: string,
  { includeHandled = false }: { includeHandled?: boolean } = {}
): Promise<Result<SuggestedQuoteChange[], ApiError>> {
  try {
    const response = await axios.get<
      SuggestedQuoteChange[] | QuickQuoteSuggestionsPayload
    >(
      `thread/${encodeURIComponent(threadID)}/quote/request/suggestions`,
      includeHandled ? { params: { includeHandled: true } } : undefined
    );
    return ok(unwrapSuggestions(response.data));
  } catch (error) {
    captureException(error, { functionName: 'getQuickQuoteSuggestion' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get quick quote suggestion' });
    }

    return err({
      message:
        error.response?.data.message || 'Failed to get quick quote suggestion',
    });
  }
}

function unwrapSuggestions(
  data: SuggestedQuoteChange[] | QuickQuoteSuggestionsPayload
): SuggestedQuoteChange[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray(data.suggestions)) {
    return data.suggestions;
  }

  return [];
}
